// FASE 05 — Cadastros Gerais (plano de 23/09/2026, seção 4).
// Página comum aos cadastros simples da Gerência, no layout do gabarito (pages/Linhas): listagem
// paginada com filtro, modal de cadastro/edição e exclusão com confirmação. Cada cadastro informa
// o endpoint, as colunas da listagem e os campos do formulário.
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { GridColDef } from "@mui/x-data-grid";
import { debounce } from "lodash";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import {
  excluirCadastro,
  listarCadastro,
  salvarCadastro,
  TIPOS_MATERIAL,
} from "services/modules/cadastrosGerais";

export interface IOpcaoCadastro {
  value: string | number;
  label: string;
}

export interface ICampoCadastro {
  nome: string;
  rotulo: string;
  tipo: "texto" | "numero" | "selecao" | "tiposMaterial" | "interruptor";
  obrigatorio?: boolean;
  maxLength?: number;
  opcoes?: IOpcaoCadastro[];
  carregarOpcoes?: () => Promise<IOpcaoCadastro[]>;
  valorPadrao?: unknown;
  ajuda?: string;
}

export interface IColunaCadastro {
  campo: string;
  titulo: string;
  flex?: number;
  filtro?: boolean;
  render?: (linha: any) => ReactNode;
}

interface PaginaCadastroGeralProps {
  titulo: string;
  rotulo: string;
  tituloListagem: string;
  tituloModal: string;
  endpoint: string;
  campoId: string;
  colunas: IColunaCadastro[];
  campos: ICampoCadastro[];
  acoesCabecalho?: ReactNode;
  /** Incrementar força o recarregamento da listagem (ex.: após alterar um cadastro auxiliar). */
  versaoDados?: number;
}

type Valores = Record<string, any>;

const valoresIniciais = (campos: ICampoCadastro[], linha?: Valores): Valores => {
  const valores: Valores = {};
  campos.forEach((campo) => {
    if (campo.tipo === "tiposMaterial") {
      TIPOS_MATERIAL.forEach(({ campo: tipo }) => {
        valores[tipo] = linha ? !!linha[tipo] : false;
      });
      return;
    }
    const padrao = campo.valorPadrao ?? (campo.tipo === "interruptor" ? true : "");
    const atual = linha?.[campo.nome];
    valores[campo.nome] = atual === undefined || atual === null ? padrao : atual;
  });
  return valores;
};

export function PaginaCadastroGeral({
  titulo,
  rotulo,
  tituloListagem,
  tituloModal,
  endpoint,
  campoId,
  colunas,
  campos,
  acoesCabecalho,
  versaoDados = 0,
}: PaginaCadastroGeralProps) {
  const { openNotification } = useNavigation();
  const [linhas, setLinhas] = useState<Valores[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [paginacao, setPaginacao] = useState({ page: 1, pageSize: 10, search: "" });

  const [modal, setModal] = useState<{ aberto: boolean; linha?: Valores }>({ aberto: false });
  const [valores, setValores] = useState<Valores>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const [opcoes, setOpcoes] = useState<Record<string, IOpcaoCadastro[]>>({});

  const [exclusao, setExclusao] = useState<{ aberto: boolean; id?: number }>({ aberto: false });
  const [excluindo, setExcluindo] = useState(false);

  const carregar = useCallback(
    async (params = paginacao) => {
      setCarregando(true);
      try {
        const pagina = await listarCadastro<Valores>(endpoint, params);
        setLinhas(pagina.itens);
        setTotal(pagina.total);
      } catch (error: any) {
        openNotification({ message: error?.message || "Não foi possível carregar a listagem", type: "error" });
      } finally {
        setCarregando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint]
  );

  useEffect(() => {
    carregar(paginacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacao, versaoDados]);

  const filtrar = useMemo(
    () =>
      debounce((texto: string) => {
        setPaginacao((atual) => ({ ...atual, page: 1, search: texto }));
      }, TIME_DEBOUNCE),
    []
  );

  const abrirModal = async (linha?: Valores) => {
    setValores(valoresIniciais(campos, linha));
    setErros({});
    setModal({ aberto: true, linha });

    const comOpcoes = campos.filter((campo) => campo.carregarOpcoes);
    if (comOpcoes.length === 0) return;
    try {
      const carregadas = await Promise.all(comOpcoes.map((campo) => campo.carregarOpcoes!()));
      const mapa: Record<string, IOpcaoCadastro[]> = {};
      comOpcoes.forEach((campo, indice) => {
        mapa[campo.nome] = carregadas[indice];
      });
      setOpcoes(mapa);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível carregar as opções", type: "error" });
    }
  };

  const validar = (): boolean => {
    const novosErros: Record<string, string> = {};
    campos.forEach((campo) => {
      if (campo.tipo === "tiposMaterial") {
        if (!TIPOS_MATERIAL.some(({ campo: tipo }) => valores[tipo])) {
          novosErros[campo.nome] = "Selecione pelo menos um tipo";
        }
        return;
      }
      const valor = valores[campo.nome];
      if (campo.obrigatorio && (valor === "" || valor === null || valor === undefined || String(valor).trim() === "")) {
        novosErros[campo.nome] = "Campo obrigatório";
      } else if (campo.tipo === "numero" && valor !== "" && valor !== null && Number.isNaN(Number(valor))) {
        novosErros[campo.nome] = "Informe um número";
      }
    });
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    const payload: Valores = { [campoId]: modal.linha?.[campoId] ?? 0 };
    campos.forEach((campo) => {
      if (campo.tipo === "tiposMaterial") {
        TIPOS_MATERIAL.forEach(({ campo: tipo }) => {
          payload[tipo] = !!valores[tipo];
        });
        return;
      }
      const valor = valores[campo.nome];
      if (campo.tipo === "numero") payload[campo.nome] = valor === "" ? null : Number(valor);
      else if (campo.tipo === "selecao") payload[campo.nome] = valor === "" ? null : valor;
      else if (campo.tipo === "texto") payload[campo.nome] = String(valor ?? "").trim();
      else payload[campo.nome] = valor;
    });

    setSalvando(true);
    try {
      await salvarCadastro(endpoint, payload);
      openNotification({ message: "Registro salvo com sucesso!", type: "success" });
      setModal({ aberto: false });
      carregar(paginacao);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível salvar o registro", type: "error" });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!exclusao.id) return;
    setExcluindo(true);
    try {
      await excluirCadastro(endpoint, campoId, exclusao.id);
      openNotification({ message: "Registro excluído com sucesso!", type: "success" });
      carregar(paginacao);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível excluir o registro", type: "error" });
    } finally {
      setExcluindo(false);
      setExclusao({ aberto: false });
    }
  };

  const colunasGrid: GridColDef[] = [
    ...colunas.map<GridColDef>((coluna) => ({
      field: coluna.campo,
      headerName: coluna.titulo,
      flex: coluna.flex ?? 1,
      sortable: false,
      renderCell: coluna.render ? (params) => coluna.render!(params.row) : undefined,
      renderHeader: coluna.filtro
        ? () => (
            <FilterColumn
              label={coluna.titulo}
              name={coluna.campo}
              onChange={(evento) => filtrar(evento.target.value)}
            />
          )
        : undefined,
    })),
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params) => (
        <FloatMenuCrud
          onEdit={() => abrirModal(params.row)}
          showDuplicateOption={false}
          deleteButtonText="Excluir"
          onDelete={() => setExclusao({ aberto: true, id: params.row[campoId] })}
        />
      ),
    },
  ];

  const renderizarCampo = (campo: ICampoCadastro) => {
    const erro = erros[campo.nome];
    const ajuda = erro ?? campo.ajuda ?? (campo.obrigatorio ? "Obrigatório" : undefined);

    if (campo.tipo === "tiposMaterial") {
      const selecionados = TIPOS_MATERIAL.filter(({ campo: tipo }) => valores[tipo]);
      return (
        <Autocomplete
          multiple
          options={[...TIPOS_MATERIAL]}
          value={selecionados}
          getOptionLabel={(opcao) => opcao.label}
          isOptionEqualToValue={(opcao, valor) => opcao.campo === valor.campo}
          onChange={(_, novos) => {
            const marcados = new Set(novos.map((n) => n.campo));
            setValores((atual) => {
              const proximo = { ...atual };
              TIPOS_MATERIAL.forEach(({ campo: tipo }) => {
                proximo[tipo] = marcados.has(tipo);
              });
              return proximo;
            });
          }}
          renderTags={(marcados, getTagProps) =>
            marcados.map((opcao, indice) => (
              <Chip label={opcao.label} color="primary" {...getTagProps({ index: indice })} key={opcao.campo} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label={campo.rotulo} error={!!erro} helperText={erro ?? "Selecione pelo menos um"} />
          )}
        />
      );
    }

    if (campo.tipo === "interruptor") {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!valores[campo.nome]}
              onChange={(evento) => setValores((atual) => ({ ...atual, [campo.nome]: evento.target.checked }))}
            />
          }
          label={campo.rotulo}
        />
      );
    }

    if (campo.tipo === "selecao") {
      const opcoesDoCampo = campo.opcoes ?? opcoes[campo.nome] ?? [];
      const valorAtual = valores[campo.nome];
      // Registro antigo com valor fora da lista (ex.: sigla ISIS nos Prenativos) aparece como está.
      const lista =
        valorAtual !== "" && valorAtual !== null && valorAtual !== undefined && !opcoesDoCampo.some((o) => o.value === valorAtual)
          ? [...opcoesDoCampo, { value: valorAtual, label: String(valorAtual) }]
          : opcoesDoCampo;
      return (
        <TextField
          select
          fullWidth
          label={campo.rotulo}
          value={valores[campo.nome] ?? ""}
          onChange={(evento) => setValores((atual) => ({ ...atual, [campo.nome]: evento.target.value }))}
          error={!!erro}
          helperText={ajuda}
        >
          {!campo.obrigatorio && (
            <MenuItem value="">
              <em>Nenhuma</em>
            </MenuItem>
          )}
          {lista.map((opcao) => (
            <MenuItem key={opcao.value} value={opcao.value}>
              {opcao.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        type={campo.tipo === "numero" ? "number" : "text"}
        label={campo.rotulo}
        value={valores[campo.nome] ?? ""}
        onChange={(evento) => setValores((atual) => ({ ...atual, [campo.nome]: evento.target.value }))}
        error={!!erro}
        helperText={ajuda}
        inputProps={{ maxLength: campo.maxLength }}
      />
    );
  };

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title={titulo}
        label={rotulo}
        buttonLabel="Cadastrar"
        buttonOnClick={() => abrirModal()}
        hasBackButton={false}
        acoesExtras={acoesCabecalho}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12}>
              <TitleTable title={tituloListagem} />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={linhas}
              columns={colunasGrid}
              getRowId={(linha: Valores) => linha[campoId]}
              page={paginacao.page - 1}
              pageSize={paginacao.pageSize}
              rowCount={total}
              loading={carregando || excluindo}
              onPageChange={(pagina) => setPaginacao((atual) => ({ ...atual, page: pagina + 1 }))}
              onPageSizeChange={(tamanho) => setPaginacao((atual) => ({ ...atual, pageSize: tamanho, page: 1 }))}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <Dialog open={modal.aberto} onClose={() => setModal({ aberto: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tituloModal}
          <IconButton
            onClick={() => setModal({ aberto: false })}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Fechar"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {campos.map((campo) => (
              <Grid item xs={12} key={campo.nome}>
                {renderizarCampo(campo)}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <Grid container justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
          <Grid item>
            <LoadingButton variant="outlined" onClick={() => setModal({ aberto: false })} disabled={salvando}>
              Cancelar
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton variant="contained" onClick={salvar} loading={salvando}>
              Salvar
            </LoadingButton>
          </Grid>
        </Grid>
      </Dialog>

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir o registro?"
        description="Essa operação não pode ser desfeita."
        open={exclusao.aberto}
        onClose={() => setExclusao({ aberto: false })}
        onConfirm={excluir}
        isLoadingDelete={excluindo}
      />
    </DashboardStackCustom>
  );
}
